import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import Delete from '@material-ui/icons/Delete';
import MovieIcon from '@mui/icons-material/Movie';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import instance, { serverURL } from '../../helpers';

const getVideoUrl = (src) => `${serverURL}/api/videos/file/${encodeURIComponent(src)}`;

const formatBytes = (bytes) => {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const rest = String(total % 60).padStart(2, '0');
  return `${minutes}:${rest}`;
};

export default function ProfileVideoList({ itemData, edit = false, setVideos, showTitle = true }) {
  const videos = useMemo(() => (Array.isArray(itemData) ? itemData.filter(Boolean) : []), [itemData]);

  const removeVideo = async (video) => {
    try {
      await instance().delete(`/videos/${video._id}`);
    } catch (error) {
      console.error('Video delete failed', error);
    }

    if (setVideos) {
      setVideos({ videos: videos.filter((item) => item._id !== video._id) });
    }
  };

  if (videos.length === 0) {
    return edit ? (
      <Box sx={{ width: '100%', mt: 2, p: { xs: 3, sm: 5 }, border: '1px dashed rgba(44, 62, 80, 0.25)', borderRadius: 3, textAlign: 'center', background: 'rgba(112, 157, 179, 0.06)' }}>
        <MovieIcon sx={{ fontSize: 52, color: '#709db3', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#182b3a' }}>
          Zatiaľ nie je pridané žiadne video
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Kliknite na tlačidlo nižšie a vyberte krátke video. Po nahratí sa automaticky optimalizuje.
        </Typography>
      </Box>
    ) : null;
  }

  return (
    <Box sx={{ width: '100%', mt: showTitle ? 4 : 0 }}>
      {showTitle && (
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#182b3a', textAlign: 'center' }}>
          Videá
        </Typography>
      )}
      <Grid container spacing={2.5} justifyContent="center">
        {videos.map((video) => (
          <Grid item xs={12} md={edit ? 6 : 10} lg={edit ? 6 : 8} key={video._id || video.src}>
            <Card sx={{ position: 'relative', overflow: 'hidden', borderRadius: 3, boxShadow: '0 16px 44px rgba(44, 62, 80, 0.12)', border: '1px solid rgba(44, 62, 80, 0.08)' }}>
              {edit && (
                <IconButton
                  aria-label="Vymazať video"
                  onClick={() => removeVideo(video)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    '&:hover': { background: 'rgba(0,0,0,0.75)' },
                  }}
                >
                  <Delete />
                </IconButton>
              )}
              <CardMedia
                component="video"
                src={getVideoUrl(video.src)}
                poster={video.posterSrc ? getVideoUrl(video.posterSrc) : undefined}
                controls
                preload="metadata"
                sx={{ width: '100%', aspectRatio: '16 / 9', background: '#111' }}
              />
              <CardContent sx={{ py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <PlayCircleOutlineIcon sx={{ color: '#40596a', fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#40596a' }}>
                  {[formatDuration(video.durationSeconds), formatBytes(video.sizeBytes), video.processedWidth && video.processedHeight ? `${video.processedWidth}×${video.processedHeight}` : '']
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
